import type React from "react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useLocaleDateFormatter } from "hooks/useLocaleDateFormatter";
import { useReactTooltip } from "hooks/useReactTooltip";

import { SortableTable } from "components/SortableTable/SortableTable";
import type { TableRow } from "components/SortableTable/SortableTable.model";

import { CssButton, CssClickableIcon } from "styles/helpers/layout";

export interface IndividualInvite {
  id: string;
  email: string;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  dateField: string | null;
  inTargetGroup?: boolean;
  isRemovable?: boolean;
}

interface IndividualInviteListProps {
  inviteList: IndividualInvite[];
  showGroupColumn?: boolean;
  readOnly?: boolean;
  dateColumnTitle?: string;
  noResultsMessage?: string;
  onDeleteInvite?: (inviteId: string) => Promise<void>;
  isLoading?: boolean;
}

export const IndividualInviteList: React.FC<IndividualInviteListProps> = ({
  inviteList = [],
  showGroupColumn = true,
  readOnly = true,
  dateColumnTitle,
  noResultsMessage,
  onDeleteInvite,
  isLoading = false,
}) => {
  useReactTooltip();
  const { t } = useTranslation(["common", "teacher-dashboard"]);
  const { formatDate } = useLocaleDateFormatter();

  const onDeleteButtonClick = useCallback(
    (inviteId: string) => {
      if (onDeleteInvite) {
        onDeleteInvite(inviteId);
      }
    },
    [onDeleteInvite],
  );

  const hasDeleteAction = !readOnly && onDeleteInvite;

  const showNameColumn = useMemo(
    () => inviteList.some((invite) => invite.firstName || invite.middleName || invite.lastName),
    [inviteList],
  );

  // Build object with information to display on rows
  const tableRowData = useMemo<TableRow[]>(
    () =>
      inviteList.map((invite) => {
        const canDeleteInvite = invite.isRemovable ?? false;

        return {
          id: invite.id,
          dataCy: "individual-invite-row",
          cells: [
            ...(showNameColumn
              ? [
                  {
                    id: `invite-${invite.id}-name`,
                    dataCy: "individual-invite-name",
                    content:
                      [invite.firstName, invite.middleName, invite.lastName]
                        .filter(Boolean)
                        .join(" ") || "-",
                  },
                ]
              : []),
            {
              id: `invite-${invite.id}-email`,
              dataCy: "individual-invite-email",
              content: invite.email,
            },
            {
              id: `invite-${invite.id}-date`,
              dataCy: "individual-invite-accepted-date",
              content: invite.dateField ? formatDate(invite.dateField, "dd-MM-yyyy") : "-",
            },
            ...(showGroupColumn
              ? [
                  {
                    id: `invite-${invite.id}-grouped`,
                    dataCy: "individual-invite-grouped",
                    content: invite.inTargetGroup
                      ? t(
                          "teacher-dashboard:invitationManagement.inviteBundleDetailsModal.individualEmailInviteList.columnValues.inTargetGroup.yes",
                        )
                      : t(
                          "teacher-dashboard:invitationManagement.inviteBundleDetailsModal.individualEmailInviteList.columnValues.inTargetGroup.no",
                        ),
                  },
                ]
              : []),
            ...(hasDeleteAction
              ? [
                  {
                    id: `invite-${invite.id}-actions`,
                    dataCy: "individual-invite-actions",
                    content: (
                      <CssButton
                        aria-label={t("common:iconButtonLabel.delete")}
                        data-cy="delete-individual-email-invite-button"
                        data-tip={
                          canDeleteInvite
                            ? t(
                                "teacher-dashboard:studentLicense.invitationList.tooltip.removableInvite",
                              )
                            : t(
                                "teacher-dashboard:studentLicense.invitationList.tooltip.removalExpired",
                              )
                        }
                        variant="text"
                        disabled={isLoading || !canDeleteInvite}
                        onClick={() => onDeleteButtonClick(invite.id)}
                      >
                        <CssClickableIcon iconName="far fa-trash-alt" />
                      </CssButton>
                    ),
                  },
                ]
              : []),
          ],
        };
      }),
    [
      inviteList,
      showGroupColumn,
      showNameColumn,
      hasDeleteAction,
      isLoading,
      formatDate,
      t,
      onDeleteButtonClick,
    ],
  );

  return (
    <SortableTable
      variant="white"
      headerCells={[
        ...(showNameColumn
          ? [
              {
                id: "individual-invite-name-column",
                title: t(
                  "teacher-dashboard:invitationManagement.inviteBundleDetailsModal.individualEmailInviteList.columns.name",
                ),
                size: "2fr",
              },
            ]
          : []),
        {
          id: "individual-invite-email-column",
          title: t(
            "teacher-dashboard:invitationManagement.inviteBundleDetailsModal.individualEmailInviteList.columns.email",
          ),
          size: "3fr",
        },
        {
          id: "individual-invite-date-column",
          title:
            dateColumnTitle ||
            t(
              "teacher-dashboard:invitationManagement.inviteBundleDetailsModal.individualEmailInviteList.columns.acceptedDate",
            ),
          size: "2fr",
        },
        // Group column is optional and controlled by the parent via showGroupColumn
        ...(showGroupColumn
          ? [
              {
                id: "individual-invite-grouped-column",
                title: t(
                  "teacher-dashboard:invitationManagement.inviteBundleDetailsModal.individualEmailInviteList.columns.inTargetGroup",
                ),
                size: "1fr",
              },
            ]
          : []),
        ...(hasDeleteAction
          ? [
              {
                id: "individual-invite-actions-column",
                title: t(
                  "teacher-dashboard:invitationManagement.inviteBundleDetailsModal.individualEmailInviteList.columns.actions",
                ),
                size: "1fr",
              },
            ]
          : []),
      ]}
      rows={tableRowData}
      noResultsMessage={
        noResultsMessage ||
        t(
          "teacher-dashboard:invitationManagement.inviteBundleDetailsModal.individualEmailInviteList.noResultsMessage",
        )
      }
    />
  );
};
